import JMuxerModule from "jmuxer/dist/jmuxer.min.js";
import { performanceStore } from "../performance/store";
import { directControl } from "./controlStore";
import type { DirectTouch } from "./protobuf";

interface Callbacks {
  onFrame: (frame: VideoFrame) => void;
  onState: (state: string) => void;
  onError: (error: Error) => void;
}

interface JMuxerInstance {
  feed: (data: { video: Uint8Array; duration?: number; isLastVideoFrameComplete?: boolean }) => void;
  destroy: () => void;
}

interface JMuxerOptions {
  node: HTMLVideoElement;
  mode: "video";
  flushingTime: number;
  maxDelay: number;
  clearBuffer: boolean;
  fps: number;
  onReady: () => void;
  onError: (error: unknown) => void;
  onUnsupportedCodec: (codec: string) => void;
}

type JMuxerConstructor = new (options: JMuxerOptions) => JMuxerInstance;

const DISPLAY_SOCKET = `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}/tesla-android/sockets/display`;
const TOUCH_SOCKET = `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}/tesla-android/sockets/touchscreen`;
const FRAME_DURATION_US = 33_333;
const FRAME_GAP_THRESHOLD_MS = 150;
const FREEZE_THRESHOLD_MS = 500;
const TOUCH_SLOT_COUNT = 10;

const keyCodes: Record<string, number> = {
  GoHome: 3, GoBack: 4, Power: 26, AppSwitch: 187,
  AudioVolumeUp: 24, AudioVolumeDown: 25,
  Enter: 66, Backspace: 67, Delete: 112, Escape: 4, Tab: 61, " ": 62,
  ArrowUp: 19, ArrowDown: 20, ArrowLeft: 21, ArrowRight: 22,
};

const androidKeyCode = (key: string) => {
  if (keyCodes[key] !== undefined) return keyCodes[key];
  if (/^[a-z]$/i.test(key)) return 29 + key.toUpperCase().charCodeAt(0) - 65;
  if (/^[0-9]$/.test(key)) return 7 + Number(key);
  return undefined;
};

const concat = (left: Uint8Array | undefined, right: Uint8Array) => {
  if (!left?.byteLength) return right.slice();
  const merged = new Uint8Array(left.byteLength + right.byteLength);
  merged.set(left);
  merged.set(right, left.byteLength);
  return merged;
};

const startCodeLength = (bytes: Uint8Array, index: number) => {
  if (bytes[index] !== 0 || bytes[index + 1] !== 0) return 0;
  if (bytes[index + 2] === 1) return 3;
  if (bytes[index + 2] === 0 && bytes[index + 3] === 1) return 4;
  return 0;
};

const splitNalUnits = (bytes: Uint8Array) => {
  const units: Uint8Array[] = [];
  let start = -1;
  for (let index = 0; index < bytes.length - 2;) {
    const length = startCodeLength(bytes, index);
    if (!length) {
      index += 1;
      continue;
    }
    if (start >= 0 && index > start) units.push(bytes.slice(start, index));
    start = index;
    index += length;
  }
  if (start >= 0) units.push(bytes.slice(start));
  return units.length ? units : [bytes];
};

const nalType = (nal: Uint8Array) => {
  const offset = startCodeLength(nal, 0);
  return offset < nal.length ? nal[offset] & 0x1f : -1;
};

const codecFromSps = (sps: Uint8Array) => {
  const offset = startCodeLength(sps, 0);
  if (offset + 3 >= sps.length) return "avc1.42E01F";
  return `avc1.${[sps[offset + 1], sps[offset + 2], sps[offset + 3]]
    .map((value) => value.toString(16).padStart(2, "0")).join("")}`;
};

export class TeslaAndroidClient {
  static webCodecsSupported() { return "VideoDecoder" in window && "EncodedVideoChunk" in window; }
  static mseSupported() { return "MediaSource" in window; }
  static supported() { return this.webCodecsSupported() || this.mseSupported(); }

  private displaySocket?: WebSocket;
  private touchSocket?: WebSocket;
  private decoder?: VideoDecoder;
  private muxer?: JMuxerInstance;
  private codecConfig?: Uint8Array;
  private codec = "avc1.42E01F";
  private configured = false;
  private configurePromise?: Promise<void>;
  private pendingKeyFrame?: Uint8Array;
  private waitingForKeyframe = true;
  private stopped = false;
  private displayReconnectTimer?: number;
  private touchReconnectTimer?: number;
  private displayStreaming = false;
  private nextTimestampUs = 0;
  private slots = new Map<number, { slot: number; trackingId: number }>();
  private nextTrackingId = 1;
  private received = 0;
  private decoded = 0;
  private dropped = 0;
  private bytesReceived = 0;
  private intervalBytes = 0;
  private intervalFrames = 0;
  private intervalStarted = performance.now();
  private submitted = new Map<number, number>();
  private decodeTotalMs = 0;
  private lastFrameAt?: number;
  private freezeCount = 0;
  private totalFreezeMs = 0;
  private maxFreezeMs = 0;
  private frameGapCount = 0;
  private lastFrameGapMs = 0;
  private maxFrameGapMs = 0;
  private controlMessages = 0;
  private controlBytes = 0;
  private lastMseTimestamp?: number;
  private msePlaying = false;
  private videoFrameCallbackId?: number;
  private lastPresentedFrames = 0;
  private mseLiveTimer?: number;

  constructor(
    private readonly callbacks: Callbacks,
    private readonly androidWidth: number,
    private readonly androidHeight: number,
    private readonly video?: HTMLVideoElement,
  ) {}

  connect() {
    this.stopped = false;
    if (!TeslaAndroidClient.supported()) {
      this.callbacks.onError(new Error("This browser cannot decode TeslaAndroid's H.264 stream"));
      return;
    }
    if (!TeslaAndroidClient.webCodecsSupported() && !this.muxer) {
      if (!this.video) {
        this.callbacks.onError(new Error("The HTTP H.264 player is unavailable"));
        return;
      }
      this.initializeMse(this.video);
    }
    this.callbacks.onState("connecting");
    performanceStore.update({
      backend: "tesla-native",
      connectionState: "connecting",
      senderCodec: "video/H264",
      senderEncoder: "TeslaAndroid native H.264",
      senderEncoderType: "hardware",
      configuredFps: 30,
      width: this.androidWidth,
      height: this.androidHeight,
      icePath: "TeslaAndroid keepalive relay · native H.264 WebSocket",
      protocol: "tcp",
      connectionMode: "authenticated HTTP media relay",
    });
    this.openDisplaySocket();
    this.openTouchSocket();
  }

  disconnect() {
    this.stopped = true;
    this.clearReconnectTimers();
    this.closeSockets();
    this.closeDecoder();
    this.destroyMse();
    this.callbacks.onState("disconnected");
  }

  reconnect() {
    this.clearReconnectTimers();
    this.closeSockets();
    this.closeDecoder();
    this.destroyMse();
    this.codecConfig = undefined;
    this.pendingKeyFrame = undefined;
    this.waitingForKeyframe = true;
    this.nextTimestampUs = 0;
    this.displayStreaming = false;
    this.lastFrameAt = undefined;
    this.connect();
  }

  setProfile(_profile: unknown) { /* TeslaAndroid owns its capture profile. */ }

  private openDisplaySocket() {
    if (this.stopped || this.displaySocket) return;
    const display = new WebSocket(DISPLAY_SOCKET);
    display.binaryType = "arraybuffer";
    this.displaySocket = display;
    display.onmessage = (event) => this.receiveDisplay(event.data);
    display.onerror = () => { /* onclose performs a seamless retry. */ };
    display.onclose = () => {
      if (this.displaySocket === display) this.displaySocket = undefined;
      this.scheduleDisplayReconnect();
    };
  }

  private openTouchSocket() {
    if (this.stopped || this.touchSocket) return;
    const touch = new WebSocket(TOUCH_SOCKET);
    this.touchSocket = touch;
    touch.onopen = () => {
      this.resetTouchscreen();
      performanceStore.update({ dataChannelState: "open" });
    };
    touch.onclose = () => {
      if (this.touchSocket === touch) this.touchSocket = undefined;
      this.slots.clear();
      performanceStore.update({ dataChannelState: "closed" });
      this.scheduleTouchReconnect();
    };
    touch.onerror = () => performanceStore.update({ dataChannelState: "unavailable" });
  }

  private closeSockets() {
    for (const socket of [this.displaySocket, this.touchSocket]) {
      if (socket) {
        socket.onclose = null;
        socket.close();
      }
    }
    this.displaySocket = this.touchSocket = undefined;
    this.slots.clear();
  }

  private clearReconnectTimers() {
    if (this.displayReconnectTimer !== undefined) window.clearTimeout(this.displayReconnectTimer);
    if (this.touchReconnectTimer !== undefined) window.clearTimeout(this.touchReconnectTimer);
    this.displayReconnectTimer = undefined;
    this.touchReconnectTimer = undefined;
  }

  private scheduleDisplayReconnect() {
    if (this.stopped || this.displayReconnectTimer !== undefined) return;
    this.displayStreaming = false;
    this.msePlaying = false;
    this.waitingForKeyframe = true;
    this.lastFrameAt = undefined;
    performanceStore.update({
      connectionState: "reconnecting",
      reconnectCount: performanceStore.getSnapshot().reconnectCount + 1,
    });
    this.callbacks.onState("reconnecting");
    this.displayReconnectTimer = window.setTimeout(() => {
      this.displayReconnectTimer = undefined;
      this.openDisplaySocket();
    }, 500);
  }

  private scheduleTouchReconnect() {
    if (this.stopped || this.touchReconnectTimer !== undefined) return;
    performanceStore.update({ dataChannelState: "reconnecting" });
    this.touchReconnectTimer = window.setTimeout(() => {
      this.touchReconnectTimer = undefined;
      this.openTouchSocket();
    }, 500);
  }

  private receiveDisplay(data: string | ArrayBuffer | Blob) {
    if (!(data instanceof ArrayBuffer)) return;
    const packet = new Uint8Array(data);
    // Parent status effects clear transport-specific fields when the backend
    // changes. Raw TeslaAndroid has no separate metadata packet, so publish
    // its immutable sender metadata with the first (and later) media packets.
    performanceStore.update({
      senderCodec: "video/H264",
      senderEncoder: "TeslaAndroid native H.264",
      senderEncoderType: "hardware",
      configuredFps: 30,
      width: this.androidWidth,
      height: this.androidHeight,
      icePath: "TeslaAndroid keepalive relay · native H.264 WebSocket",
      protocol: "tcp",
      connectionMode: "authenticated HTTP media relay",
    });
    this.bytesReceived += packet.byteLength;
    this.intervalBytes += packet.byteLength;
    for (const nal of splitNalUnits(packet)) this.receiveNal(nal);
    this.updateStats();
  }

  private receiveNal(nal: Uint8Array) {
    const type = nalType(nal);
    if (type === 7) {
      this.codecConfig = nal.slice();
      this.codec = codecFromSps(nal);
      if (TeslaAndroidClient.webCodecsSupported()) void this.configureDecoder();
      else this.feedMse(nal, false);
      return;
    }
    if (type === 8) {
      this.codecConfig = concat(this.codecConfig, nal);
      if (!TeslaAndroidClient.webCodecsSupported()) this.feedMse(nal, false);
      return;
    }
    if (type !== 1 && type !== 5) return;
    const key = type === 5;
    this.received += 1;
    if (this.waitingForKeyframe && !key) {
      this.dropped += 1;
      return;
    }
    if (!TeslaAndroidClient.webCodecsSupported()) {
      this.feedMse(nal, key);
      return;
    }
    if (!this.configured || !this.decoder || this.decoder.state !== "configured") {
      if (key) this.pendingKeyFrame = nal.slice();
      void this.configureDecoder();
      return;
    }
    this.decode(nal, key);
  }

  private configureDecoder() {
    if (this.configured) return Promise.resolve();
    if (this.configurePromise) return this.configurePromise;
    this.configurePromise = this.configureDecoderOnce().finally(() => { this.configurePromise = undefined; });
    return this.configurePromise;
  }

  private async configureDecoderOnce() {
    if (!this.codecConfig) return;
    if (!this.decoder) {
      this.decoder = new VideoDecoder({
        output: (frame) => {
          this.recordFrameTiming();
          const submitted = this.submitted.get(frame.timestamp);
          if (submitted !== undefined) {
            this.decodeTotalMs += performance.now() - submitted;
            this.submitted.delete(frame.timestamp);
          }
          this.decoded += 1;
          this.intervalFrames += 1;
          this.callbacks.onFrame(frame);
          this.updateStats();
          if (!this.displayStreaming) {
            this.displayStreaming = true;
            performanceStore.update({ connectionState: "connected" });
            this.callbacks.onState("streaming");
          }
        },
        error: (error) => {
          this.configured = false;
          this.waitingForKeyframe = true;
          this.submitted.clear();
          this.callbacks.onError(error);
        },
      });
    }
    try {
      const preferred: VideoDecoderConfig = {
        codec: this.codec,
        codedWidth: this.androidWidth,
        codedHeight: this.androidHeight,
        optimizeForLatency: true,
        hardwareAcceleration: "prefer-hardware",
      };
      const hardware = await VideoDecoder.isConfigSupported(preferred);
      const selected: VideoDecoderConfig = hardware.supported
        ? preferred
        : { ...preferred, hardwareAcceleration: "no-preference" };
      const support = hardware.supported ? hardware : await VideoDecoder.isConfigSupported(selected);
      if (!support.supported) throw new Error(`Browser cannot decode TeslaAndroid H.264 profile ${this.codec}`);
      this.decoder.configure(selected);
      this.configured = true;
      performanceStore.update({
        decoderImplementation: hardware.supported
          ? "WebCodecs · hardware preferred"
          : "WebCodecs · browser-selected fallback",
      });
      const pending = this.pendingKeyFrame;
      this.pendingKeyFrame = undefined;
      if (pending) this.decode(pending, true);
    } catch (error) {
      this.callbacks.onError(error instanceof Error ? error : new Error(String(error)));
    }
  }

  private decode(nal: Uint8Array, key: boolean) {
    if (!this.decoder || this.decoder.state !== "configured") return;
    if (this.decoder.decodeQueueSize > 4 && !key) {
      this.dropped += 1;
      return;
    }
    if (key) this.waitingForKeyframe = false;
    const timestamp = this.nextTimestampUs;
    this.nextTimestampUs += FRAME_DURATION_US;
    this.submitted.set(timestamp, performance.now());
    try {
      this.decoder.decode(new EncodedVideoChunk({
        type: key ? "key" : "delta",
        timestamp,
        duration: FRAME_DURATION_US,
        data: key ? concat(this.codecConfig, nal) : nal,
      }));
    } catch (error) {
      this.dropped += 1;
      this.waitingForKeyframe = true;
      this.callbacks.onError(error instanceof Error ? error : new Error(String(error)));
    }
  }

  private closeDecoder() {
    if (this.decoder && this.decoder.state !== "closed") this.decoder.close();
    this.decoder = undefined;
    this.configured = false;
    this.configurePromise = undefined;
    this.submitted.clear();
  }

  private initializeMse(video: HTMLVideoElement) {
    const JMuxer = JMuxerModule as JMuxerConstructor | undefined;
    if (typeof JMuxer !== "function") throw new Error("Media Source H.264 player failed to load");
    video.muted = true;
    video.addEventListener("canplay", this.playMse);
    video.addEventListener("playing", this.markMseStreaming);
    this.muxer = new JMuxer({
      node: video,
      mode: "video",
      flushingTime: 0,
      maxDelay: 80,
      clearBuffer: true,
      fps: 30,
      onReady: this.playMse,
      onError: (error) => this.callbacks.onError(error instanceof Error ? error : new Error(String(error))),
      onUnsupportedCodec: (codec) => this.callbacks.onError(new Error(`Browser cannot decode ${codec}`)),
    });
    performanceStore.update({ decoderImplementation: "Media Source Extensions · native H.264" });
    this.mseLiveTimer = window.setInterval(this.keepMseAtLiveEdge, 200);
    if ("requestVideoFrameCallback" in video) {
      this.videoFrameCallbackId = video.requestVideoFrameCallback(this.trackMseFrame);
    }
  }

  private feedMse(nal: Uint8Array, key: boolean) {
    if (!this.muxer) return;
    if (key) this.waitingForKeyframe = false;
    const timestamp = this.nextTimestampUs;
    if (nalType(nal) === 1 || key) this.nextTimestampUs += FRAME_DURATION_US;
    const previous = this.lastMseTimestamp;
    this.lastMseTimestamp = timestamp;
    this.muxer.feed({
      video: key ? concat(this.codecConfig, nal) : nal,
      duration: previous === undefined ? 33 : Math.max(1, Math.min(250, Math.round((timestamp - previous) / 1000) || 33)),
      isLastVideoFrameComplete: nalType(nal) === 1 || key,
    });
  }

  private readonly playMse = () => {
    void this.video?.play().catch((error) => this.callbacks.onError(new Error(String(error))));
  };

  private readonly markMseStreaming = () => {
    performanceStore.update({ connectionState: "connected" });
    if (!this.msePlaying) {
      this.msePlaying = true;
      this.displayStreaming = true;
      this.callbacks.onState("streaming");
    }
  };

  private readonly trackMseFrame: VideoFrameRequestCallback = (_now, metadata) => {
    const presented = metadata.presentedFrames || this.lastPresentedFrames + 1;
    const increment = Math.max(1, presented - this.lastPresentedFrames);
    this.lastPresentedFrames = presented;
    this.decoded += increment;
    this.intervalFrames += increment;
    const quality = this.video?.getVideoPlaybackQuality?.();
    if (quality) this.dropped = Math.max(this.dropped, quality.droppedVideoFrames);
    this.recordFrameTiming();
    this.updateStats();
    this.markMseStreaming();
    if (!this.stopped && this.video && "requestVideoFrameCallback" in this.video) {
      this.videoFrameCallbackId = this.video.requestVideoFrameCallback(this.trackMseFrame);
    }
  };

  private readonly keepMseAtLiveEdge = () => {
    const video = this.video;
    if (!video?.buffered.length) return;
    const liveEdge = video.buffered.end(video.buffered.length - 1);
    const lag = liveEdge - video.currentTime;
    if (lag > 0.3) video.currentTime = Math.max(0, liveEdge - 0.04);
    video.playbackRate = lag > 0.1 ? 1.08 : 1;
  };

  private destroyMse() {
    this.muxer?.destroy();
    this.muxer = undefined;
    this.msePlaying = false;
    if (this.mseLiveTimer !== undefined) window.clearInterval(this.mseLiveTimer);
    this.mseLiveTimer = undefined;
    if (this.video) {
      this.video.removeEventListener("canplay", this.playMse);
      this.video.removeEventListener("playing", this.markMseStreaming);
      if (this.videoFrameCallbackId !== undefined && "cancelVideoFrameCallback" in this.video) {
        this.video.cancelVideoFrameCallback(this.videoFrameCallbackId);
      }
      this.video.pause();
      this.video.playbackRate = 1;
      this.video.removeAttribute("src");
      this.video.load();
    }
    this.videoFrameCallbackId = undefined;
    this.lastMseTimestamp = undefined;
    this.lastPresentedFrames = 0;
  }

  private recordFrameTiming() {
    const now = performance.now();
    if (document.visibilityState !== "visible") {
      this.lastFrameAt = now;
      return;
    }
    if (this.lastFrameAt !== undefined) {
      const gap = now - this.lastFrameAt;
      this.lastFrameGapMs = gap;
      this.maxFrameGapMs = Math.max(this.maxFrameGapMs, gap);
      if (gap >= FRAME_GAP_THRESHOLD_MS) this.frameGapCount += 1;
      if (gap >= FREEZE_THRESHOLD_MS) {
        this.freezeCount += 1;
        this.totalFreezeMs += gap;
        this.maxFreezeMs = Math.max(this.maxFreezeMs, gap);
      }
    }
    this.lastFrameAt = now;
  }

  private updateStats() {
    const now = performance.now();
    const elapsed = now - this.intervalStarted;
    const total = this.decoded + this.dropped;
    const values: Parameters<typeof performanceStore.update>[0] = {
      codec: this.codec,
      framesReceived: this.received,
      framesDecoded: this.decoded,
      framesDropped: this.dropped,
      bytesReceived: this.bytesReceived,
      droppedFramePercent: total ? Math.round(this.dropped / total * 10_000) / 100 : 0,
      averageDecodeTimeMs: this.decoded ? Math.round(this.decodeTotalMs / this.decoded * 100) / 100 : 0,
      decoderQueueSize: this.decoder?.decodeQueueSize || 0,
      freezeCount: this.freezeCount,
      totalFreezeDurationMs: Math.round(this.totalFreezeMs * 10) / 10,
      maxFreezeDurationMs: Math.round(this.maxFreezeMs * 10) / 10,
      frameGapCount: this.frameGapCount,
      lastFrameGapMs: Math.round(this.lastFrameGapMs * 10) / 10,
      maxFrameGapMs: Math.round(this.maxFrameGapMs * 10) / 10,
    };
    if (elapsed >= 1000) {
      values.fps = Math.round(this.intervalFrames * 1000 / elapsed * 10) / 10;
      values.bitrateMbps = Math.round(this.intervalBytes * 8 / elapsed / 1000 * 100) / 100;
      this.intervalFrames = 0;
      this.intervalBytes = 0;
      this.intervalStarted = now;
    }
    performanceStore.update(values);
  }

  sendTouch(touches: DirectTouch[], essential = false) {
    if (!directControl.canHumanControl()) return;
    let payload = "";
    for (const touch of touches) {
      const down = touch.pressure > 0;
      let contact = this.slots.get(touch.identifier);
      if (down && !contact) {
        const used = new Set([...this.slots.values()].map((item) => item.slot));
        const slot = Array.from({ length: TOUCH_SLOT_COUNT }, (_, index) => index).find((index) => !used.has(index));
        if (slot === undefined) continue;
        contact = { slot, trackingId: this.nextTrackingId++ };
        this.slots.set(touch.identifier, contact);
        payload += `s ${slot}\nT ${contact.trackingId}\na 1\nX ${touch.x}\nY ${touch.y}\ne 0\nS 0\n`;
      } else if (down && contact) {
        payload += `s ${contact.slot}\nX ${touch.x}\nY ${touch.y}\ne 0\nS 0\n`;
      } else if (!down && contact) {
        payload += `s ${contact.slot}\nT -1\na 0\ne 0\nS 0\n`;
        this.slots.delete(touch.identifier);
      }
    }
    this.sendTouchPayload(payload, essential);
  }

  sendScroll(x: number, y: number, deltaX: number, deltaY: number) {
    if (!directControl.canHumanControl()) return;
    const slot = Array.from({ length: TOUCH_SLOT_COUNT }, (_, index) => index)
      .find((index) => ![...this.slots.values()].some((item) => item.slot === index));
    if (slot === undefined) return;
    const trackingId = this.nextTrackingId++;
    const distance = Math.min(360, Math.max(96, Math.round(Math.hypot(deltaX, deltaY))));
    const horizontal = Math.abs(deltaX) > Math.abs(deltaY);
    const endX = Math.max(0, Math.min(this.androidWidth - 1, x - (horizontal ? Math.sign(deltaX) * distance : 0)));
    const endY = Math.max(0, Math.min(this.androidHeight - 1, y - (!horizontal ? Math.sign(deltaY) * distance : 0)));
    let payload = `s ${slot}\nT ${trackingId}\na 1\nX ${x}\nY ${y}\ne 0\nS 0\n`;
    for (let step = 1; step <= 5; step += 1) {
      const nextX = Math.round(x + (endX - x) * step / 5);
      const nextY = Math.round(y + (endY - y) * step / 5);
      payload += `s ${slot}\nX ${nextX}\nY ${nextY}\ne 0\nS 0\n`;
    }
    payload += `s ${slot}\nT -1\na 0\ne 0\nS 0\n`;
    this.sendTouchPayload(payload, true);
  }

  sendKey(key: string, eventType: 0 | 1 | 2 = 2) {
    if (!directControl.canHumanControl() || eventType === 0) return;
    const code = androidKeyCode(key);
    if (code === undefined) return;
    void this.postControl("/api/key", { keyCode: code });
  }

  rotate() {
    if (!directControl.canHumanControl()) return;
    void this.postControl("/api/rotate", {});
  }

  private resetTouchscreen() {
    this.slots.clear();
    let payload = "";
    for (let slot = 0; slot < TOUCH_SLOT_COUNT; slot += 1) payload += `s ${slot}\nT -1\na 0\n`;
    payload += "e 0\nS 0\n";
    this.sendTouchPayload(payload, true);
  }

  private sendTouchPayload(payload: string, essential: boolean) {
    const socket = this.touchSocket;
    if (!payload || !socket || socket.readyState !== WebSocket.OPEN) return;
    if (!essential && socket.bufferedAmount > 16 * 1024) return;
    socket.send(payload);
    this.controlMessages += 1;
    this.controlBytes += new TextEncoder().encode(payload).byteLength;
    performanceStore.update({
      dataMessagesSent: this.controlMessages,
      dataBytesSent: this.controlBytes,
      dataBufferedAmount: socket.bufferedAmount,
    });
  }

  private async postControl(path: string, body: unknown) {
    try {
      const response = await fetch(path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
    } catch (error) {
      this.callbacks.onError(new Error(`TeslaAndroid control failed: ${String(error)}`));
    }
  }
}
